import { create, StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';

export type StoreState = {
  cleanup?(): void;
  clear(): void;
};

export type StateValues<TState extends StoreState> = Omit<
  TState,
  PropertiesOfType<TState, Function>
>;

export type StateActions<TState extends StoreState> = Pick<
  TState,
  PropertiesOfType<TState, Function>
>;

export type DefinedActions<TState extends StoreState> = Omit<StateActions<TState>, 'clear'>;

export type SetStateFn<TState extends StoreState> = Parameters<StateCreator<TState>>[0];

export type GetStoreActionsFn<TState extends StoreState> = (
  set: SetStateFn<TState>,
  get: () => TState,
) => DefinedActions<TState>;

export const createStore = <TState extends StoreState>(
  name: string,
  initialState: StateValues<TState>,
  getActions: GetStoreActionsFn<TState>,
) =>
  create<TState>()(
    devtools(
      (set, get) => {
        const actions = getActions(set, get);

        return <TState>{
          ...initialState,
          ...actions,
          clear: () =>
            set(({ cleanup, clear }) => {
              cleanup?.();
              return <TState>{ ...initialState, ...actions, cleanup, clear };
            }, true),
        };
      },
      { name },
    ),
  );
